import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import "../styles/Header/header.css";
import User from "../assets/images/Users.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../api/apiServices";
import {
  notificationList,
  readNotificationById,
  readAllNotifications,
} from "../api/apiList";
import Loader from "../components/Loader";
import { MutableRefObject } from "react";
import Toast from "../utils/Toast";
import { SidebarContext } from "../hooks/UseSidebar";
import ConfirmationDialog from "../components/DailogBox";
import { useAuth } from "../hooks/useAuth";
import { Role } from "../constant/RoleConstants";
import { roleBasedRedirection } from "../utils/helper";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Label from "../components/Common/Label";
import Popup from "../components/Common/Popup";

const drawerWidth = 100;

interface HeaderProps {
  pageName?: string;
  isShow?: boolean;
  className?: string;
  headerRef?: MutableRefObject<null>;
}

interface NotificationProps {
  id: number;
  text: string;
  createdAt: string;
  jobId: number;
}

const Header: React.FC<HeaderProps> = ({
  isShow,
  pageName,
  className,
  headerRef,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [markingReadId, setMarkingReadId] = useState<number | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const { logout, user } = useAuth();
  const { toggleSide, side } = useContext(SidebarContext);
  const [openDialog, setOpenDialog] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node)
    ) {
      handlePopoverClose();
    }
  };

  // Changed from hover to click
  const handlePopoverToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (user?.status !== 1 && location.pathname !== "/subscriptions") {
      fetchNotification();
    }
  }, []);

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationToggle = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (notificationAnchorEl) {
      handleNotificationClose();
    } else {
      setNotificationAnchorEl(event.currentTarget);
      fetchNotification();
    }
  };

  const fetchNotification = async () => {
    const response = await postRequest(notificationList);
    if (response?.data?.success) {
      const unread = response?.data?.data?.notification || [];
      setNotifications(unread);
      setNotificationCount(response?.data?.data?.count ?? unread.length);
    }
  };

  const removeNotificationFromList = (id: number) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    setNotificationCount((prev) => Math.max(prev - 1, 0));
  };

  const markNotificationAsRead = async (
    notification: NotificationProps,
    options?: { navigateToJob?: boolean }
  ) => {
    if (markingReadId === notification.id) return false;

    setMarkingReadId(notification.id);
    try {
      const response = await postRequest(readNotificationById(notification.id));
      if (response?.data?.success) {
        removeNotificationFromList(notification.id);
        if (options?.navigateToJob && notification.jobId) {
          handleNotificationClose();
          navigate(
            roleBasedRedirection(`/jobs/update-job/${notification.jobId}`)
          );
        }
        return true;
      }
      return false;
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleCompleteReadNotification = async (
    notification: NotificationProps
  ) => {
    await markNotificationAsRead(notification, { navigateToJob: true });
  };

  const handleDismissNotification = async (
    event: React.MouseEvent,
    notification: NotificationProps
  ) => {
    event.stopPropagation();
    await markNotificationAsRead(notification);
  };

  const handleMarkAllAsRead = async () => {
    if (!notifications.length || markingAllRead) return;

    setMarkingAllRead(true);
    try {
      const response = await postRequest(readAllNotifications);
      if (response?.data?.success) {
        setNotifications([]);
        setNotificationCount(0);
      }
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleEditUserProfile = () => {
    navigate(roleBasedRedirection(`/update-user-profile/${user?.uId}`));
    handlePopoverClose();
  };

  const handleBillingClick = () => {
    navigate(roleBasedRedirection(`/billings`));
    handlePopoverClose();
  };

  const handleEditProfile = () => {
    navigate(`/update-company-profile/${user?.uId}`);
    handlePopoverClose();
  };

  const handleOpenConfirmationDialog = () => {
    setOpenDialog(true);
  };

  const handleLogout = () => {
    logout();
    Toast("Logout Successfully.");
    navigate("/login");
    handlePopoverClose();
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const isMobile = useMediaQuery("(max-width:500px)");
  const isTabs = useMediaQuery("(max-width:1200px)");

  const toggleSidebar = () => {
    toggleSide();
  };

  return isShow ? (
    <>
      <Box
        position="static"
        className={className}
        ref={headerRef}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          flexDirection: "row",
          marginTop: isTabs ? "0px" : "20px",
        }}
      >
        <Box className="header-main" sx={{ flexGrow: 1 }}>
          {/* Hamburger Menu - Only visible on tablets and mobile */}
          {isTabs && (
            <IconButton
              onClick={toggleSidebar}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                color: "#fff",
                marginRight: 2,
                boxShadow: "0 4px 12px rgba(132, 225, 204, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #4ab89a 0%, #6fd4ba 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(132, 225, 204, 0.5)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant={isTabs ? "h5" : "h4"}
            component="div"
            sx={{
              marginLeft: isTabs ? "0px" : "50px",
              fontWeight: 700,
              fontSize: isTabs ? "24px" : "32px",
              color: "#273848",
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
            }}
          >
            {pageName}
          </Typography>
        </Box>

        {/* User Informations */}
        <Container
          style={{
            display: "flex",
            justifyContent: "flex-end",
            width: "auto",
          }}
          className="header-main header-box"
        >
          {user?.status !== 1 && (
            <>
              <IconButton
                onClick={handleNotificationToggle}
                sx={{
                  width: 48,
                  height: 48,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)",
                  border: "2px solid rgba(132, 225, 204, 0.3)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                    borderColor: "#84E1CC",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(132, 225, 204, 0.3)",
                  },
                }}
              >
                <Badge
                  badgeContent={notificationCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "11px",
                      minWidth: "20px",
                      height: "20px",
                      borderRadius: "10px",
                      border: "2px solid #fff",
                    },
                  }}
                >
                  <NotificationsIcon
                    sx={{
                      color: "#56c6a9",
                      fontSize: 24,
                    }}
                  />
                </Badge>
              </IconButton>

              <Popover
                open={notificationOpen}
                anchorEl={notificationAnchorEl}
                onClose={handleNotificationClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    mt: 1.5,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                    border: "1px solid rgba(132, 225, 204, 0.25)",
                  },
                }}
              >
                <Box className="notification-panel">
                  <Box className="notification-panel-header">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <p className="notification-panel-title">Notifications</p>
                      {notificationCount > 0 && (
                        <span className="notification-panel-count">
                          {notificationCount} unread
                        </span>
                      )}
                    </Box>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        className="notification-panel-mark-all"
                        onClick={handleMarkAllAsRead}
                        disabled={markingAllRead}
                      >
                        {markingAllRead ? "Clearing..." : "Mark all read"}
                      </button>
                    )}
                  </Box>

                  <Box className="notification-panel-list">
                    {notifications.length ? (
                      notifications.map((notification) => (
                        <Box
                          key={notification.id}
                          component="button"
                          type="button"
                          className="notification-item"
                          onClick={() =>
                            handleCompleteReadNotification(notification)
                          }
                          disabled={markingReadId === notification.id}
                        >
                          <span className="notification-item-unread-dot" />
                          <Box className="notification-item-content">
                            <p className="notification-item-text">
                              {notification.text}
                            </p>
                            <span className="notification-item-meta">
                              Unread · Tap to open
                            </span>
                          </Box>
                          <button
                            type="button"
                            className="notification-dismiss-btn"
                            title="Mark as read"
                            onClick={(e) =>
                              handleDismissNotification(e, notification)
                            }
                            disabled={markingReadId === notification.id}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </button>
                        </Box>
                      ))
                    ) : (
                      <Box className="notification-panel-empty">
                        <NotificationsIcon
                          sx={{ color: "#cbd5e1", fontSize: 40 }}
                        />
                        <p className="notification-panel-empty-title">
                          No unread notifications
                        </p>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Popover>
            </>
          )}

          {isTabs ? (
            <>
              <Box
                onClick={handlePopoverToggle}
                sx={{
                  cursor: "pointer",
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid #84E1CC",
                  boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 6px 16px rgba(132, 225, 204, 0.5)",
                    borderColor: "#56c6a9",
                  },
                }}
              >
                <img
                  src={user?.profile || User}
                  alt="User"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              {/* Dropdown */}
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: "16px",
                    width: "200px",
                    mt: 1.5,
                    overflow: "hidden",
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                    border: "1px solid rgba(132, 225, 204, 0.3)",
                    background: "#ffffff",
                  },
                }}
              >
                <List sx={{ padding: "8px" }}>
                  <ListItem
                    button
                    onClick={handleEditUserProfile}
                    sx={{
                      borderRadius: "10px",
                      marginBottom: "4px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <ListItemText
                      primary="Profile"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#273848",
                        },
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    onClick={handleBillingClick}
                    sx={{
                      borderRadius: "10px",
                      marginBottom: "4px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <ListItemText
                      primary="Billings"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#273848",
                        },
                      }}
                    />
                  </ListItem>
                  {user?.status == Role.RECRUITER && (
                    <ListItem
                      button
                      onClick={handleEditProfile}
                      sx={{
                        borderRadius: "10px",
                        marginBottom: "4px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="Company Profile"
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#273848",
                          },
                        }}
                      />
                    </ListItem>
                  )}
                  <Box
                    sx={{
                      height: "1px",
                      background: "rgba(132, 225, 204, 0.3)",
                      margin: "8px 8px",
                    }}
                  />
                  <ListItem
                    button
                    onClick={handleOpenConfirmationDialog}
                    sx={{
                      borderRadius: "10px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <ListItemText
                      primary="Logout"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#ef4444",
                        },
                      }}
                    />
                  </ListItem>
                </List>
              </Popover>
            </>
          ) : (
            <>
              <Grid
                direction="row"
                display="flex"
                alignItems="center"
                borderRadius="24px"
                padding="6px 8px 6px 20px"
                position="relative"
                onClick={handlePopoverToggle}
                width={"auto"}
                minWidth={"220px"}
                sx={{
                  cursor: "pointer",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)",
                  border: "2px solid rgba(132, 225, 204, 0.3)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#84E1CC",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(132, 225, 204, 0.3)",
                    background:
                      "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  {/* User Personal Information */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#273848",
                        lineHeight: 1.3,
                      }}
                    >
                      {user?.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#56c6a9",
                        lineHeight: 1.3,
                      }}
                    >
                      {user?.status == Role.RECRUITER
                        ? "Recruiter"
                        : "Hiring Manager"}
                    </Typography>
                  </Box>
                  {/* User Image */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "3px solid #84E1CC",
                      boxShadow: "0 2px 8px rgba(132, 225, 204, 0.3)",
                    }}
                  >
                    <img
                      src={user?.profile || User}
                      alt="User"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Box>
                {/* Dropdown */}
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: "16px",
                      width: "200px",
                      mt: 1.5,
                      overflow: "hidden",
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                      border: "1px solid rgba(132, 225, 204, 0.3)",
                      background: "#ffffff",
                    },
                  }}
                >
                  <List sx={{ padding: "8px" }}>
                    <ListItem
                      button
                      onClick={handleEditUserProfile}
                      sx={{
                        borderRadius: "10px",
                        marginBottom: "4px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="Profile"
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#273848",
                          },
                        }}
                      />
                    </ListItem>
                    {user?.status == Role.RECRUITER && (
                      <ListItem
                        button
                        onClick={handleBillingClick}
                        sx={{
                          borderRadius: "10px",
                          marginBottom: "4px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <ListItemText
                          primary="Billings"
                          primaryTypographyProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#273848",
                            },
                          }}
                        />
                      </ListItem>
                    )}
                    {user?.status == Role.RECRUITER && (
                      <ListItem
                        button
                        onClick={handleEditProfile}
                        sx={{
                          borderRadius: "10px",
                          marginBottom: "4px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #f0fdf9 0%, #e6f9f4 100%)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <ListItemText
                          primary="Company Profile"
                          primaryTypographyProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#273848",
                            },
                          }}
                        />
                      </ListItem>
                    )}
                    <Box
                      sx={{
                        height: "1px",
                        background: "rgba(132, 225, 204, 0.3)",
                        margin: "8px 8px",
                      }}
                    />
                    <ListItem
                      button
                      onClick={handleOpenConfirmationDialog}
                      sx={{
                        borderRadius: "10px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{
                          sx: {
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#ef4444",
                          },
                        }}
                      />
                    </ListItem>
                  </List>
                </Popover>
              </Grid>
            </>
          )}
        </Container>
      </Box>
      {/* <ConfirmationDialog
        open={openDialog}
        onConfirm={handleLogout}
        onCancel={handleClose}
        title="Logout Confirmation"
        message="Are you sure you want to log out?"
        cancelButtonName="Cancel"
        confirmButtonName="Logout"
      /> */}
      <Popup
        isOpen={openDialog}
        onClose={handleClose}
        onYes={() => {
          handleLogout();
        }}
        onNo={handleClose}
        popupName="Logout Confirmation"
        popupContain="Are you sure you want to log out?"
        noButtonText="Cancel"
        yesButtonText="Logout"
        yesButtonClass="primary-btn"
      />
    </>
  ) : (
    <span></span>
  );
};
export default Header;
